// src/components/Layout/Navigation.js
// Responsive tab bar: on narrow viewports (tablet/mobile) the tab row scrolls
// horizontally with touch/wheel instead of clipping, and the active tab is
// automatically brought into view. The page itself never scrolls sideways.
import React, { useEffect, useRef } from 'react';
import { useBudget } from '../../context/BudgetContext';
// Import the navigation CSS from styles folder
import '../../styles/navigation.css';

const Navigation = () => {
  const { state, actions } = useBudget();
  const currentPage = state.currentPage || 'home';
  const isCalculatorOpen = state.isCalculatorOpen || false;
  const listRef = useRef(null);

  const navigationItems = [
    { id: 'home', label: 'Home', icon: '🏠' },
    { id: 'income', label: 'Income', icon: '💵' },
    { id: 'weekly', label: 'Weekly', icon: '🗓️' },
    { id: 'monthly', label: 'Monthly', icon: '📅' },
    { id: 'annual', label: 'Annual', icon: '📆' },
    { id: 'planner', label: 'Planner', icon: '📋' },
    { id: 'accounts', label: 'Accounts', icon: '🏦' },
    { id: 'reports', label: 'Reports', icon: '📊' },
    { id: 'notes', label: 'Notes', icon: '📝' },
    { id: 'import', label: 'Data', icon: '💾' },
    { id: 'links', label: 'Links', icon: '🔗' }
  ];

  // Keep the active tab visible whenever the route changes or the viewport
  // resizes (e.g. rotating an iPad). scrollIntoView with inline:'nearest'
  // only scrolls the tab row, never the page.
  useEffect(() => {
    const scrollActiveIntoView = () => {
      const activeTab = listRef.current?.querySelector('.nav-link.active');
      if (activeTab && typeof activeTab.scrollIntoView === 'function') {
        activeTab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    };
    scrollActiveIntoView();
    window.addEventListener('resize', scrollActiveIntoView);
    return () => window.removeEventListener('resize', scrollActiveIntoView);
  }, [currentPage]);

  const handleNavigation = (pageId) => {
    actions.setCurrentPage(pageId);
  };

  const handleCalculatorToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    actions.toggleCalculator();
  };

  return (
    <nav className="main-navigation" role="navigation" aria-label="Main navigation">
      <div className="nav-container">
        {/* Main Navigation Links (horizontally scrollable when they overflow) */}
        <ul className="nav-links" role="menubar" ref={listRef}>
          {navigationItems.map((item) => (
            <li key={item.id} className="nav-item" role="none">
              <button
                className={`nav-link ${currentPage === item.id ? 'active' : ''}`}
                onClick={() => handleNavigation(item.id)}
                role="menuitem"
                aria-current={currentPage === item.id ? 'page' : undefined}
                title={`Navigate to ${item.label}`}
              >
                <span className="nav-icon" aria-hidden="true">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            </li>
          ))}
        </ul>

        {/* Calculator Toggle - stays pinned outside the scrollable row */}
        <div className="nav-right">
          <button
            className={`calculator-nav-btn ${isCalculatorOpen ? 'active' : ''}`}
            onClick={handleCalculatorToggle}
            title={isCalculatorOpen ? 'Close Calculator (Alt+C)' : 'Open Calculator (Alt+C)'}
            aria-label={isCalculatorOpen ? 'Close Calculator' : 'Open Calculator'}
            aria-pressed={isCalculatorOpen}
          >
            <span className="nav-icon" aria-hidden="true">🔢</span>
            <span className="nav-label">Calculator</span>
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
